import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useAuth } from '../contexts/AuthContext'
import Layout from '../components/layout/Layout'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import ChangePasswordModal from '../components/settings/ChangePasswordModal'
import DeleteAccountModal from '../components/settings/DeleteAccountModal'
import AnalyticsView from '../components/settings/AnalyticsView'
import NotificationSettings from '../components/settings/NotificationSettings'
import PrivacySettings from '../components/settings/PrivacySettings'
import FamilySettings from '../components/settings/FamilySettings'
import ChildViewSettings from '../components/settings/ChildViewSettings'
import KioskSettings from '../components/settings/KioskSettings'
import ExportDataModal from '../components/settings/ExportDataModal'

const {
  FiSettings,
  FiUser,
  FiLock,
  FiTrash2,
  FiBarChart3,
  FiBell,
  FiShield,
  FiUsers,
  FiEye,
  FiTablet,
  FiDownload,
  FiHelpCircle,
  FiLogOut,
} = FiIcons

const SettingsPage = () => {
  const { user, family, signOut } = useAuth()
  const [activeTab, setActiveTab] = useState('general')
  const [showChangePassword, setShowChangePassword] = useState(false)
  const [showDeleteAccount, setShowDeleteAccount] = useState(false)
  const [showExportData, setShowExportData] = useState(false)

  const settingsTabs = [
    { id: 'general', label: 'General', icon: FiSettings, component: FamilySettings },
    { id: 'kiosk', label: 'Kiosk Mode', icon: FiTablet, component: KioskSettings },
    { id: 'children', label: 'Child Views', icon: FiEye, component: ChildViewSettings },
    { id: 'analytics', label: 'Analytics', icon: FiBarChart3, component: AnalyticsView },
    { id: 'notifications', label: 'Notifications', icon: FiBell, component: NotificationSettings },
    { id: 'privacy', label: 'Privacy', icon: FiShield, component: PrivacySettings },
  ]

  const quickActions = [
    {
      title: 'Change Password',
      description: 'Update your account password',
      icon: FiLock,
      action: () => setShowChangePassword(true),
      color: 'text-blue-600',
    },
    {
      title: 'Export Data',
      description: "Download your family's data",
      icon: FiDownload,
      action: () => setShowExportData(true),
      color: 'text-green-600',
    },
    {
      title: 'Sign Out',
      description: 'Sign out of your account',
      icon: FiLogOut,
      action: signOut,
      color: 'text-orange-600',
    },
    {
      title: 'Delete Account',
      description: 'Permanently delete your account',
      icon: FiTrash2,
      action: () => setShowDeleteAccount(true),
      color: 'text-red-600',
    },
  ]

  const ActiveComponent = settingsTabs.find((tab) => tab.id === activeTab)?.component

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Settings</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manage your family account and preferences</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <Card className="p-0 overflow-hidden">
              {/* Profile Section */}
              <div className="p-6 bg-gradient-to-r from-primary-500 to-secondary-500 text-white">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiUser} className="text-xl" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{family?.name || 'Family'}</h3>
                    <p className="text-white/80 text-sm">{user?.email}</p>
                  </div>
                </div>
              </div>

              {/* Navigation Tabs */}
              <div className="p-2">
                {settingsTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all duration-200
                      ${activeTab === tab.id
                        ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 border-l-4 border-primary-500'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }
                    `}
                  >
                    <SafeIcon icon={tab.icon} className="text-lg" />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                ))}
              </div>

              {/* Quick Actions */}
              <div className="p-4 border-t border-gray-100 dark:border-gray-700">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Quick Actions</h4>
                <div className="space-y-2">
                  {quickActions.map((action, index) => (
                    <button
                      key={index}
                      onClick={action.action}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <SafeIcon icon={action.icon} className={`text-sm ${action.color}`} />
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{action.title}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{action.description}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              {ActiveComponent && <ActiveComponent />}
            </motion.div>
          </div>
        </div>

        {/* Modals */}
        <ChangePasswordModal 
          isOpen={showChangePassword} 
          onClose={() => setShowChangePassword(false)} 
        />
        <DeleteAccountModal 
          isOpen={showDeleteAccount} 
          onClose={() => setShowDeleteAccount(false)} 
        />
        <ExportDataModal 
          isOpen={showExportData} 
          onClose={() => setShowExportData(false)} 
        />
      </div>
    </Layout>
  )
}

export default SettingsPage